import { CustomMutationOptions, CustomQueryOptions } from '@/types/query';
import {
  GetServerlessProjectByIdRequest,
  GetServerlessProjectByIdResponse,
  GetServerlessProjectsResponse,
  GetServerlessTaskByIdResponse,
  GetServerlessTaskByIdRequest,
  ListServerlessTasksRequest,
  ListServerlessTasksResponse,
  GetServerlessTaskLogsResponse,
  GetServerlessTaskLogsRequest,
  GetServerlessEndpointByIdRequest,
  GetServerlessEndpointByIdResponse,
  ServerlessRegenerateApiKeyRequest,
  ServerlessRegenerateApiKeyResponse,
  ServerlessStartEndpointRequest,
  ServerlessStopEndpointRequest,
  ServerlessGetTokenRequest,
  ServerlessGetTokenResponse,
  GetServerlessStorageRequest,
  GetServerlessStorageResponse,
  DeleteServerlessProjectRequest,
  ServerlessTaskSpotByIdRequest,
  ServerlessTaskSpotByIdResponse,
  ServerlessTaskSpotStopRequest,
  ServerlessTaskSpotUpdateRequest,
  RemoveFileOnRemoteParams,
  DownloadFileOnRemoteParams,
  DownloadFileOnRemoteResponse,
  UploadFileOnRemoteResponse,
  UploadFileOnRemoteParams,
  GetServerlessStorageSizeResponse,
  GetServerlessStorageSizeRequest,
  GetServerlessMetricsEndpointResponse,
  GetServerlessMetricsEndpointRequest,

  GetServerlessMetricsDailyTasksRequest,
  GetServerlessMetricsDailyCostRequest,
  GetServerlessMetricsDailyTasksResponse,
  GetServerlessMetricsDailyCostResponse,
  GetServerlessMetricsTotalConsumptionRequest,
  GetServerlessMetricsTotalConsumptionResponse,

  GetServerlessInstancePricingResponse,
  ServerlessStorageCopyParams,
  ServerlessStorageCopyResponse,
  GetServerlessInstanceDefaultResponse,
} from './type';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteServerlessProject,
  downloadFileOnRemote,
  getServerlessMetricsDailyTasks,
  getServerlessEndpointById,
  getServerlessMetricsEndpoint,
  getServerlessInstancePricing,
  getServerlessProjectById,
  getServerlessProjects,
  getServerlessStorage,
  getServerlessStorageSize,
  getServerlessTaskById,
  getServerlessTaskLogs,
  getServerlessTaskSpotById,
  getServerlessToken,
  listServerlessTasks,
  regenerateApiKey,
  removeFileOnRemote,
  startEndpoint,
  stopEndpoint,
  stopServerlessTaskSpot,
  updateServerlessTaskSpot,
  uploadFileOnRemote,
  getServerlessMetricsDailyCost,
  getServerlessMetricsTotalConsumption,
  getServerlessStorageCopy,
  getServerlessInstanceDefault,
} from '.';
import dayjs from '@/utils/dayjs';
import { compareDateDesc } from '@/utils/date';

const keyname = 'serverless-gpu';

// สร้าง object สำหรับเก็บ query keys ทั้งหมด
export const serverlessKeys = {
  all: [keyname] as const,
  projects: () => [...serverlessKeys.all, 'projects'] as const,
  project: (params: GetServerlessProjectByIdRequest) =>
    [...serverlessKeys.all, 'project', params] as const,
  tasks: (params: ListServerlessTasksRequest) =>
    [...serverlessKeys.all, 'tasks', params] as const,
  taskById: (params: GetServerlessTaskByIdRequest) =>
    [...serverlessKeys.all, 'task-id', params] as const,
  taskLogs: (params: GetServerlessTaskLogsRequest) =>
    [...serverlessKeys.all, 'task-logs', params] as const,
  endpoint: (params: GetServerlessEndpointByIdRequest) =>
    [...serverlessKeys.all, 'endpoint', params] as const,
  token: (params: ServerlessGetTokenRequest) =>
    [...serverlessKeys.all, 'token', params] as const,
  storage: {
    list: (params: GetServerlessStorageRequest) =>
      [...serverlessKeys.all, 'storage', params] as const,
    size: (params: GetServerlessStorageSizeRequest) =>
      [...serverlessKeys.all, 'storage-size', params] as const,
  },
  taskSpotById: (params: ServerlessTaskSpotByIdRequest) =>
    [...serverlessKeys.all, 'task-id-spot', params] as const,
  metrics: {
    endpoint: (params: GetServerlessMetricsEndpointRequest) =>
      [...serverlessKeys.all, 'metrics-endpoint', params] as const,
    totalConsumption: (params: GetServerlessMetricsTotalConsumptionRequest) =>
      [...serverlessKeys.all, 'metrics-total-consumption', params] as const,
    dailyTasks: (params: GetServerlessMetricsDailyTasksRequest) =>
      [...serverlessKeys.all, 'metrics-daily-tasks', params] as const,
    dailyCost: (params: GetServerlessMetricsDailyCostRequest) =>
      [...serverlessKeys.all, 'metrics-daily-cost', params] as const,
  },
  instancePricing: () => [...serverlessKeys.all, 'instance-pricing'] as const,
  instanceDefault: () => [...serverlessKeys.all, 'instance-default'] as const,
} as const;

export const useGetServerlessProjects = (
  option?: CustomQueryOptions<GetServerlessProjectsResponse>
) => {
  return useQuery({
    queryKey: serverlessKeys.projects(),
    queryFn: async () => getServerlessProjects(),
    select: (data) => {
      return {
        ...data,
        projects: [...data.projects].sort(compareDateDesc('created_at')),
      };
    },
    ...option,
  });
};

export const useGetServerlessProjectById = (
  params: GetServerlessProjectByIdRequest,
  option?: CustomQueryOptions<GetServerlessProjectByIdResponse>
) => {
  return useQuery({
    queryKey: serverlessKeys.project(params),
    queryFn: async () => getServerlessProjectById(params),
    ...option,
    staleTime: 1000 * 10,
  });
};

export const useDeleteServerlessProject = (
  option?: CustomMutationOptions<DeleteServerlessProjectRequest>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteServerlessProject,
    ...option,
    onSuccess: async (data, variables, context) => {
      await option?.onSuccess?.(data, variables, context);
      await queryClient.invalidateQueries({
        // queryKey: serverlessKeys.projects(),
        queryKey: serverlessKeys.project(variables),
      });
    },
  });
};

export const useListServerlessTasks = (
  params: ListServerlessTasksRequest,
  option?: CustomQueryOptions<ListServerlessTasksResponse>
) => {
  return useQuery({
    queryKey: serverlessKeys.tasks(params),
    queryFn: async () => listServerlessTasks(params),
    ...option,
  });
};

export const useGetServerlessTaskById = (
  params: GetServerlessTaskByIdRequest,
  option?: CustomQueryOptions<GetServerlessTaskByIdResponse>
) => {
  return useQuery({
    queryKey: serverlessKeys.taskById(params),
    queryFn: async () => getServerlessTaskById(params),
    ...option,
  });
};

export const useGetServerlessTaskLogs = (
  params: GetServerlessTaskLogsRequest,
  option?: CustomQueryOptions<GetServerlessTaskLogsResponse>
) => {
  return useQuery({
    queryKey: serverlessKeys.taskLogs(params),
    queryFn: async () => getServerlessTaskLogs(params),
    ...option,
  });
};

export const useGetServerlessEndpointById = (
  params: GetServerlessEndpointByIdRequest,
  option?: CustomQueryOptions<GetServerlessEndpointByIdResponse>
) => {
  return useQuery({
    queryKey: serverlessKeys.endpoint(params),
    queryFn: async () => getServerlessEndpointById(params),
    ...option,
  });
};

export const useRegenerateApiKey = (
  option?: CustomMutationOptions<ServerlessRegenerateApiKeyRequest>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: regenerateApiKey,
    ...option,
    onSuccess: async (data, variables, context) => {
      await option?.onSuccess?.(data, variables, context);
      await queryClient.invalidateQueries({
        queryKey: serverlessKeys.endpoint({ project_id: variables.project_id }),
      });
    },
  });
};

export const useStartEndpoint = (
  option?: CustomMutationOptions<ServerlessStartEndpointRequest>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: startEndpoint,
    ...option,
    onSuccess: async (data, variables, context) => {
      await option?.onSuccess?.(data, variables, context);
      await queryClient.invalidateQueries({
        queryKey: serverlessKeys.endpoint({ project_id: variables.project_id }),
      });
    },
  });
};

export const useStopEndpoint = (
  option?: CustomMutationOptions<ServerlessStopEndpointRequest>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: stopEndpoint,
    ...option,
    onSuccess: async (data, variables, context) => {
      await option?.onSuccess?.(data, variables, context);
      await queryClient.invalidateQueries({
        queryKey: serverlessKeys.endpoint({ project_id: variables.project_id }),
      });
    },
  });
};

//get token
export const useGetServerlessToken = (
  params: ServerlessGetTokenRequest,
  option?: CustomQueryOptions<ServerlessGetTokenResponse>
) => {
  return useQuery({
    queryKey: serverlessKeys.token(params),
    queryFn: async () => getServerlessToken(params),
    ...option,
  });
};

export const useGetServerlessStorage = (
  params: GetServerlessStorageRequest,
  option?: CustomQueryOptions<GetServerlessStorageResponse>
) => {
  return useQuery({
    queryKey: serverlessKeys.storage.list(params),
    queryFn: async () => getServerlessStorage(params),
    ...option,
  });
};

export const useGetServerlessStorageSize = (
  params: GetServerlessStorageSizeRequest,
  option?: CustomQueryOptions<GetServerlessStorageSizeResponse>
) => {
  return useQuery({
    queryKey: serverlessKeys.storage.size(params),
    queryFn: async () => getServerlessStorageSize(params),
    ...option,
  });
};



export const useDownloadFileOnRemote = (
  option?: CustomMutationOptions<DownloadFileOnRemoteParams,DownloadFileOnRemoteResponse>
) => {
  return useMutation({
    mutationFn: downloadFileOnRemote,
    ...option,
  });
};

export const useUploadFileOnRemote = (
  option?: CustomMutationOptions<UploadFileOnRemoteParams,UploadFileOnRemoteResponse>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...option,
    mutationFn: uploadFileOnRemote,
    onSuccess: async (data, variables, context) => {
      await option?.onSuccess?.(data, variables, context);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: serverlessKeys.storage.list({ project_id: variables.project_id }),
        }),
        queryClient.invalidateQueries({
          queryKey: serverlessKeys.storage.size({ project_id: variables.project_id }),
        }),
      ]);
    },
  });
};
export const useRemoveFileOnRemote = (
  option?: CustomMutationOptions<RemoveFileOnRemoteParams>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeFileOnRemote,
    ...option,
    onSuccess: async (data, variables, context) => {
      await option?.onSuccess?.(data, variables, context);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: serverlessKeys.storage.list({ project_id: variables.project_id }),
        }),
        queryClient.invalidateQueries({
          queryKey: serverlessKeys.storage.size({ project_id: variables.project_id }),
        }),
      ]);
    },
  });
};

export const useServerlessStorageCopy = (
  option?: CustomMutationOptions<ServerlessStorageCopyParams,ServerlessStorageCopyResponse>
) => {
  return useMutation({
    mutationFn: getServerlessStorageCopy,
    ...option,
  });
};

export const useGetServerlessTaskSpotById = (
  params: ServerlessTaskSpotByIdRequest,
  option?: CustomQueryOptions<ServerlessTaskSpotByIdResponse>
) => {
  return useQuery({
    queryKey: serverlessKeys.taskSpotById(params),
    queryFn: async () => getServerlessTaskSpotById(params),
    ...option,
  });
};

export const useStopServerlessTaskSpot = (
  option?: CustomMutationOptions<ServerlessTaskSpotStopRequest>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: stopServerlessTaskSpot,
    ...option,
    onSuccess: async (data, variables, context) => {
      await option?.onSuccess?.(data, variables, context);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: serverlessKeys.taskById({ task_id: variables.task_id }),
        }),
        queryClient.invalidateQueries({
          queryKey: serverlessKeys.taskSpotById({ task_id: variables.task_id }),
        }),
      ]);
    },
  });
};

export const useUpdateServerlessTaskSpot = (
  option?: CustomMutationOptions<ServerlessTaskSpotUpdateRequest>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateServerlessTaskSpot,
    ...option,
    onSuccess: async (data, variables, context) => {
      await option?.onSuccess?.(data, variables, context);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: serverlessKeys.taskSpotById({ task_id: variables.task_id }),
        }),
      ]);
    },
  });
};

export const useGetServerlessMetricsEndpoint = (
  params: GetServerlessMetricsEndpointRequest,
  option?: CustomQueryOptions<GetServerlessMetricsEndpointResponse>
) => {
  return useQuery({
    queryKey: serverlessKeys.metrics.endpoint(params),
    queryFn: async () => getServerlessMetricsEndpoint(params),
    ...option,
  });
};

export const useGetServerlessMetricsTotalConsumption = (
  params: GetServerlessMetricsTotalConsumptionRequest,
  option?: CustomQueryOptions<GetServerlessMetricsTotalConsumptionResponse>
) => {
  return useQuery({
    queryKey: serverlessKeys.metrics.totalConsumption(params),
    queryFn: async () => getServerlessMetricsTotalConsumption(params),
    ...option,
  });
};

export const useGetServerlessMetricsDailyTasks = (
  params: GetServerlessMetricsDailyTasksRequest,
  option?: CustomQueryOptions<GetServerlessMetricsDailyTasksResponse>
) => {
  return useQuery({
    queryKey: serverlessKeys.metrics.dailyTasks(params),
    queryFn: async () => getServerlessMetricsDailyTasks(params),
    ...option,
  });
};

export const useGetServerlessMetricsDailyCost = (
  params: GetServerlessMetricsDailyCostRequest,
  option?: CustomQueryOptions<GetServerlessMetricsDailyCostResponse>
) => {
  return useQuery({
    queryKey: serverlessKeys.metrics.dailyCost(params),
    queryFn: async () => getServerlessMetricsDailyCost(params),
    ...option,
  });
};

export const useGetServerlessInstancePricing = (
  option?: CustomQueryOptions<GetServerlessInstancePricingResponse['data']>
) => {
  return useQuery({
    queryKey: serverlessKeys.instancePricing(),
    queryFn: async () => getServerlessInstancePricing(),
    ...option,
  });
};

export const useGetServerlessInstanceDefault = (
  option?: CustomQueryOptions<GetServerlessInstanceDefaultResponse['data']>
) => {
  return useQuery({
    queryKey: serverlessKeys.instanceDefault(),
    queryFn: async () => getServerlessInstanceDefault(),
    ...option,
  });
};
